import { useEffect, useState, type ComponentProps, type ReactNode } from "react";
import { WorkspaceMarkdownLink } from "@/components/common/WorkspaceMarkdownLink";
import {
	environmentFileContentUrl,
	lookupEnvironmentFileByPath,
	type EnvironmentFileDto,
} from "@/lib/api";
import { openEnvironmentFileByPath, toHomeDirRelative } from "@/lib/environment-files";
import { filePathFromMarkdownHref } from "@/lib/markdown-file-link";
import { selectEffectiveEnvironmentId, useChatStore } from "@/stores/useChatStore";

const IMAGE_CLASS =
	"my-1.5 block max-h-[360px] max-w-full rounded-4 object-contain shadow-[0_0_0_1px_var(--line-1)]";

/**
 * Markdown の画像。ワークスペースのファイルを指すものは環境のファイル API から読み込んで表示する。
 * 表示できないときは、押すと右ペインでファイルを開くリンクにする。
 */
export function WorkspaceMarkdownImage({ src, alt }: ComponentProps<"img">) {
	const environmentId = useChatStore(selectEffectiveEnvironmentId);
	const filePath = typeof src === "string" ? filePathFromMarkdownHref(src) : null;
	if (filePath === null) return <img src={src} alt={alt} loading="lazy" className={IMAGE_CLASS} />;

	const relativePath = toHomeDirRelative(filePath);
	const fallback = <WorkspaceMarkdownLink href={src}>{alt || filePath}</WorkspaceMarkdownLink>;
	if (!environmentId || !relativePath) return fallback;

	return (
		<EnvironmentImage
			key={`${environmentId}:${relativePath}`}
			environmentId={environmentId}
			relativePath={relativePath}
			alt={alt}
			fallback={fallback}
		/>
	);
}

interface EnvironmentImageProps {
	environmentId: string;
	relativePath: string;
	alt?: string;
	fallback: ReactNode;
}

function EnvironmentImage({ environmentId, relativePath, alt, fallback }: EnvironmentImageProps) {
	// undefined は読み込み中、null は表示できない。
	const [file, setFile] = useState<EnvironmentFileDto | null | undefined>(undefined);

	useEffect(() => {
		let cancelled = false;
		lookupEnvironmentFileByPath(environmentId, relativePath).then(
			(found) => {
				if (!cancelled) setFile(found);
			},
			(err: unknown) => {
				// 画像の代わりにファイルへのリンクを出し、押せば開けなかった理由をトーストで伝える。
				console.error("Failed to look up markdown image", err);
				if (!cancelled) setFile(null);
			},
		);
		return () => {
			cancelled = true;
		};
	}, [environmentId, relativePath]);

	if (file === null) return fallback;
	if (file === undefined) return null;

	return (
		<img
			src={environmentFileContentUrl(environmentId, file.id, { version: file.updatedAt })}
			alt={alt}
			title="クリックでファイルを開く"
			loading="lazy"
			className={`${IMAGE_CLASS} cursor-pointer`}
			onError={() => setFile(null)}
			onClick={() => {
				openEnvironmentFileByPath(relativePath).catch((err: unknown) => {
					console.error("Failed to open markdown image", err);
				});
			}}
		/>
	);
}
